import { useState } from "react";
import type { Asset } from "@yushi-portfolio/content-schema";
import type { ApiClient } from "../lib/api";
import { useAssets } from "../lib/assets";
import { ACCEPT, assetStatus, checkFile, thumbnailUrl, uploadFile } from "../lib/upload";

const TYPE_LABEL: Record<Asset["type"], string> = { image: "画像", video: "映像", document: "資料" };

export function AssetThumb({ asset, size = 96 }: { asset: Asset | undefined; size?: number }) {
  const { mediaBaseUrl } = useAssets();
  const url = asset ? thumbnailUrl(asset, mediaBaseUrl) : undefined;
  const label = !asset ? "?" : asset.type === "video" ? "▶ 映像" : asset.type === "document" ? "PDF" : assetStatus(asset) === "processing" ? "変換中…" : "";
  return (
    <div className="thumb" style={{ width: size, height: size }}>
      {url ? <img src={url} alt="" loading="lazy" /> : <span>{label}</span>}
    </div>
  );
}

export function assetLabel(asset: Asset | undefined, id: string): string {
  return asset?.originalFilename ?? asset?.id ?? `見つからないアセット (${id})`;
}

type Job = { name: string; progress: number; error?: string; done?: boolean };

/** Multi-file upload with per-file progress. Calls onUploaded for each registered asset. */
export function UploadButton({ api, onUploaded, label = "アップロード" }: { api: ApiClient; onUploaded?: (asset: Asset) => void; label?: string }) {
  const { upsert } = useAssets();
  const [jobs, setJobs] = useState<Job[]>([]);

  async function start(files: FileList | null) {
    if (!files?.length) return;
    const list = [...files];
    const offset = jobs.length;
    setJobs((j) => [...j, ...list.map((f) => ({ name: f.name, progress: 0, error: checkFile(f) }))]);
    for (const [i, file] of list.entries()) {
      if (checkFile(file)) continue;
      const update = (patch: Partial<Job>) => setJobs((j) => j.map((job, k) => (k === offset + i ? { ...job, ...patch } : job)));
      try {
        const asset = await uploadFile(api, file, (progress) => update({ progress }));
        upsert(asset);
        onUploaded?.(asset);
        update({ progress: 1, done: true });
      } catch (e) {
        update({ error: (e as Error).message });
      }
    }
  }

  return (
    <div className="stack-tight">
      <label className="button primary file-button">
        {label}
        <input type="file" multiple accept={ACCEPT} onChange={(e) => { void start(e.target.files); e.target.value = ""; }} />
      </label>
      {jobs.length > 0 && (
        <ul className="jobs">
          {jobs.map((job, i) => (
            <li key={i} className={job.error ? "error" : ""}>
              <span className="job-name">{job.name}</span>
              {job.error ? <span>{job.error}</span> : job.done ? <span>完了(変換は自動で進みます)</span> : <progress value={job.progress} max={1} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Modal grid for choosing an asset; uploading from here selects the new asset. */
export function AssetPicker({ api, onSelect, onClose }: { api: ApiClient; onSelect: (asset: Asset) => void; onClose: () => void }) {
  const { assets, error } = useAssets();
  const [filter, setFilter] = useState<"all" | Asset["type"]>("all");
  const shown = (assets ?? []).filter((a) => filter === "all" || a.type === filter);

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <header className="row-head">
          <strong>アセットを選択</strong>
          <button type="button" onClick={onClose}>閉じる</button>
        </header>
        <div className="row-head">
          <div className="segmented">
            {(["all", "image", "video", "document"] as const).map((f) => (
              <button key={f} type="button" className={filter === f ? "active" : ""} onClick={() => setFilter(f)}>
                {f === "all" ? "すべて" : TYPE_LABEL[f]}
              </button>
            ))}
          </div>
          <UploadButton api={api} label="新しくアップロード" onUploaded={(asset) => onSelect(asset)} />
        </div>
        {error && <div className="notice error">{error}</div>}
        {!assets && <p className="muted">読み込み中…</p>}
        <div className="asset-grid">
          {shown.map((asset) => (
            <button key={asset.id} type="button" className="asset-card" onClick={() => onSelect(asset)}>
              <AssetThumb asset={asset} size={120} />
              <span className="asset-name">{assetLabel(asset, asset.id)}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export { TYPE_LABEL };
